import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Award, Star, Trophy, Zap } from 'lucide-react';
import { getUserLevel, calculateProfileCompletion } from '@/lib/gamification';

interface ProfileBadgesProps {
  profileId: string;
  profileData: any;
}

export function ProfileBadges({ profileId, profileData }: ProfileBadgesProps) {
  const [userLevel, setUserLevel] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [profileCompletion, setProfileCompletion] = useState(0);

  useEffect(() => {
    if (profileId) {
      fetchUserLevel();
    }
    
    if (profileData) {
      const completion = calculateProfileCompletion(profileData);
      setProfileCompletion(completion);
    }
  }, [profileId, profileData]);

  const fetchUserLevel = async () => {
    setLoading(true);
    try {
      const levelData = await getUserLevel(profileId);
      setUserLevel(levelData);
    } catch (error) {
      console.error('Error fetching user level:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {userLevel && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="secondary" className="flex items-center gap-1 px-2 py-1">
                <Trophy className="h-3 w-3 text-amber-500" />
                <span>Level {userLevel.level}</span>
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="w-64 p-0">
              <div className="p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="font-medium">Level {userLevel.level}</span>
                  <span className="text-xs text-muted-foreground">{userLevel.points} points</span>
                </div>
                <Progress value={userLevel.progress} className="h-2 mb-1" />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{userLevel.points} pts</span>
                  <span>{userLevel.nextLevelPoints} pts</span>
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {userLevel && userLevel.level >= 3 && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="secondary" className="flex items-center gap-1 px-2 py-1">
                <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
                <span>{userLevel.title}</span>
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-sm">Achieved {userLevel.title} status by reaching Level {userLevel.level}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {profileCompletion >= 80 && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="secondary" className="flex items-center gap-1 px-2 py-1">
                <Award className="h-3 w-3 text-blue-500" />
                <span>Complete Profile</span>
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-sm">Profile is {profileCompletion}% complete</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {userLevel && userLevel.points >= 500 && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="secondary" className="flex items-center gap-1 px-2 py-1">
                <Zap className="h-3 w-3 text-purple-500" />
                <span>Power User</span>
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-sm">Earned over 500 points on the platform</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}